import { useMemo } from 'react';
import {
  Report,
  Test,
  TestFilter,
  TestResult,
  TestRun,
  Totals,
  TotalTypes,
  TestsRunsCountByHour,
  FetchReportApi,
} from '@elementary/common-ui/types';
import { distinctArray } from '@elementary/common-ui/utils/common';
import { useAuth } from '@frontegg/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CLOUD_TESTS,
  CLOUD_TESTS_TESTS,
  CLOUD_TESTS_RESULTS,
  CLOUD_TESTS_RUNS,
  CLOUD_TESTS_RUNS_COUNT_BY_DATE,
  CLOUD_TESTS_RUNS_TOTALS,
  CLOUD_TESTS_RESULTS_TOTALS,
  REPORT,
  REPORT_MERGED,
} from '@elementary/common-ui/constants/queryKeys';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import * as api from '../services/api';

const mergeFilters = (
  cloudTestTotals: { [key: string]: Totals },
  reportFilters?: TestFilter[],
) =>
  ['failures', 'warnings', 'errors', 'passed', 'no_test'].map((filter_name) => {
    const filter = reportFilters?.find(
      (report_filter) => report_filter.name === filter_name,
    ) || {
      name: filter_name,
      display_name: filter_name,
      model_unique_ids: [],
    };

    if (filter.name === 'no_test') {
      return {
        ...filter,
        model_unique_ids: filter.model_unique_ids.filter(
          (modelUniqueId) => !(modelUniqueId in cloudTestTotals),
        ),
      };
    }

    const modelsIdsWithTestRunsErrors = Object.keys(cloudTestTotals).filter(
      (modelKey) => cloudTestTotals[modelKey][filter.name as TotalTypes] > 0,
    );

    return {
      ...filter,
      model_unique_ids: distinctArray([
        ...(filter?.model_unique_ids || []),
        ...modelsIdsWithTestRunsErrors,
      ]),
    };
  });

const mergeTotals = (
  cloudTestTotals: Record<string, Totals>,
  reportTotals: Record<string, Totals> = {},
) => {
  const modelIds = distinctArray([
    ...Object.keys(cloudTestTotals),
    ...Object.keys(reportTotals),
  ]);

  const finalTotals: Record<string, Totals> = {};

  modelIds.forEach((id) => {
    const cloudItemTotals = cloudTestTotals[id];
    const reportItemTotals = reportTotals[id];
    finalTotals[id] = {
      errors: 0,
      passed: 0,
      warnings: 0,
      failures: 0,
      noData: 0,
    };

    (['errors', 'passed', 'warnings', 'failures'] as TotalTypes[]).forEach(
      (status: TotalTypes) => {
        finalTotals[id][status] =
          (cloudItemTotals?.[status] || 0) + (reportItemTotals?.[status] || 0);
      },
    );
  });

  return finalTotals;
};

const combineTests = (
  cloudReport: FetchReportApi,
  cloudTests?: Array<Test>,
) => {
  if (!cloudReport) return {};
  const combinedTests: Record<string, Test> = Object.keys(
    cloudReport.tests,
  ).reduce((acc, key) => {
    acc[key] = cloudReport.tests[key];
    return acc;
  }, {} as Record<string, Test>);

  cloudTests?.forEach((test) => {
    const modelId = test.unique_id;

    if (modelId) {
      combinedTests[modelId] = test;
    }
  });

  return combinedTests;
};

const combineTestsResults = (
  cloudReport: FetchReportApi,
  cloudTestsResults?: Array<TestResult>,
) => {
  if (!cloudReport) return {};

  const combinedResults: Record<string, TestResult[]> = Object.keys(
    cloudReport.test_results,
  ).reduce((acc, key) => {
    acc[key] = [...(cloudReport.test_results[key] || [])];
    return acc;
  }, {} as Record<string, TestResult[]>);

  cloudTestsResults?.forEach((test) => {
    const modelId = test.metadata.model_unique_id;

    if (modelId) {
      if (combinedResults[modelId]) {
        combinedResults[modelId].push(test);
      } else {
        combinedResults[modelId] = [test];
      }
    }
  });

  return combinedResults;
};

const combineTestRuns = (
  cloudReport: FetchReportApi,
  cloudTestRuns?: Array<TestRun>,
) => {
  if (!cloudReport) return {};

  const combinedRuns: Record<string, TestRun[]> = Object.keys(
    cloudReport.test_runs,
  ).reduce((acc, key) => {
    acc[key] = [...(cloudReport.test_runs[key] || [])];
    return acc;
  }, {} as Record<string, TestRun[]>);

  cloudTestRuns?.forEach((test) => {
    const modelId = test.metadata.model_unique_id;

    if (modelId) {
      if (combinedRuns[modelId]) {
        combinedRuns[modelId].push(test);
      } else {
        combinedRuns[modelId] = [test];
      }
    }
  });

  return combinedRuns;
};

const combineTestRunsTotals = (
  cloudReport: FetchReportApi,
  testsRunsTotals?: Record<string, Totals>,
) =>
  testsRunsTotals
    ? mergeTotals(testsRunsTotals, cloudReport.test_runs_totals)
    : cloudReport.test_runs_totals;

const combineTestResultsTotals = (
  cloudReport: FetchReportApi,
  testsResultsTotals?: Record<string, Totals>,
) =>
  testsResultsTotals
    ? mergeTotals(testsResultsTotals, cloudReport.test_results_totals)
    : cloudReport.test_results_totals;

const combineTestRunsFilters = (
  cloudReport: FetchReportApi,
  testsRunsTotals?: Record<string, Totals>,
) =>
  testsRunsTotals
    ? mergeFilters(testsRunsTotals, cloudReport.filters.test_runs)
    : cloudReport.filters.test_runs;

const combineTestResultsFilters = (
  cloudReport: FetchReportApi,
  testsResultsTotals?: Record<string, Totals>,
) =>
  testsResultsTotals
    ? mergeFilters(testsResultsTotals, cloudReport.filters.test_results)
    : cloudReport.filters.test_results;

// Combines the report with the cloud tests fetched data
const mergeReport = async (
  reportData: FetchReportApi,
  cloudTests?: Array<Test>,
  cloudTestsResults?: Array<TestResult>,
  cloudTestRuns?: Array<TestRun>,
  testsRunsCountByHour?: TestsRunsCountByHour,
  testsRunsTotals?: Record<string, Totals>,
  testsResultsTotals?: Record<string, Totals>,
) => {
  const updatedReport: Report = { ...reportData };
  updatedReport.tests = combineTests(updatedReport, cloudTests);
  updatedReport.test_results = combineTestsResults(
    updatedReport,
    cloudTestsResults,
  );
  updatedReport.test_runs = combineTestRuns(updatedReport, cloudTestRuns);
  updatedReport.test_runs_counts_by_hour = testsRunsCountByHour;
  updatedReport.test_runs_totals = combineTestRunsTotals(
    updatedReport,
    testsRunsTotals,
  );
  updatedReport.test_results_totals = combineTestResultsTotals(
    updatedReport,
    testsResultsTotals,
  );
  updatedReport.filters = {
    ...updatedReport.filters,
    test_results: combineTestResultsFilters(
      updatedReport,
      testsResultsTotals,
    ) as TestFilter[],
    test_runs: combineTestRunsFilters(
      updatedReport,
      testsRunsTotals,
    ) as TestFilter[],
  };

  return updatedReport;
};

const useReport = () => {
  const selectedEnvId = useSelectedEnv();
  const hasPermission = useEnvPermission();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: cloudTests, isFetching: cloudTestsFetching } = useQuery(
    [CLOUD_TESTS, selectedEnvId, CLOUD_TESTS_TESTS],
    () => api.fetchCloudTests(selectedEnvId!),
    {
      enabled: !!selectedEnvId && hasPermission('VIEW_TESTS'),
      onSuccess: () => {
        queryClient.invalidateQueries([REPORT_MERGED, selectedEnvId]);
      },
    },
  );

  const { data: cloudTestsResults, isFetching: cloudTestsResultsFetching } =
    useQuery(
      [CLOUD_TESTS, selectedEnvId, CLOUD_TESTS_RESULTS],
      () => api.fetchCloudTestsResults(selectedEnvId!),
      {
        enabled: !!selectedEnvId && hasPermission('VIEW_TESTS'),
        onSuccess: () => {
          queryClient.invalidateQueries([REPORT_MERGED, selectedEnvId]);
        },
      },
    );

  const { data: cloudTestRuns, isFetching: cloudTestRunsFetching } = useQuery(
    [CLOUD_TESTS, selectedEnvId, CLOUD_TESTS_RUNS],
    () => api.fetchCloudTestsRuns(selectedEnvId!),
    {
      enabled: !!selectedEnvId && hasPermission('VIEW_TESTS'),
      onSuccess: () => {
        queryClient.invalidateQueries([REPORT_MERGED, selectedEnvId]);
      },
    },
  );

  const {
    data: testsRunsCountByHour,
    isFetching: testsRunsCountByHourFetching,
  } = useQuery(
    [CLOUD_TESTS, selectedEnvId, CLOUD_TESTS_RUNS_COUNT_BY_DATE],
    () => api.fetchTestsRunsCountByHour(selectedEnvId!),
    {
      enabled: !!selectedEnvId && hasPermission('VIEW_TESTS'),
      onSuccess: () => {
        queryClient.invalidateQueries([REPORT_MERGED, selectedEnvId]);
      },
    },
  );

  const { data: testsRunsTotals, isFetching: testsRunsTotalsFetching } =
    useQuery(
      [CLOUD_TESTS, selectedEnvId, CLOUD_TESTS_RUNS_TOTALS],
      () => api.fetchTestRunsTotals(selectedEnvId!),
      {
        enabled: !!selectedEnvId && hasPermission('VIEW_TESTS'),
        onSuccess: () => {
          queryClient.invalidateQueries([REPORT_MERGED, selectedEnvId]);
        },
      },
    );

  const { data: testsResultsTotals, isFetching: testsResultsTotalsFetching } =
    useQuery(
      [CLOUD_TESTS, selectedEnvId, CLOUD_TESTS_RESULTS_TOTALS],
      () => api.fetchTestResultsTotals(selectedEnvId!),
      {
        enabled: !!selectedEnvId && hasPermission('VIEW_TESTS'),
        onSuccess: () => {
          queryClient.invalidateQueries([REPORT_MERGED, selectedEnvId]);
        },
      },
    );

  const {
    data: report,
    isFetching: reportFetching,
    isError,
  } = useQuery(
    [REPORT, selectedEnvId],
    () => api.fetchReport(selectedEnvId!, user?.accessToken),
    {
      enabled: !!selectedEnvId && !!user,
    },
  );

  const isFetching = useMemo(
    () =>
      reportFetching ||
      cloudTestsFetching ||
      cloudTestsResultsFetching ||
      cloudTestRunsFetching ||
      testsRunsCountByHourFetching ||
      testsResultsTotalsFetching ||
      testsRunsTotalsFetching,
    [
      cloudTestRunsFetching,
      cloudTestsFetching,
      cloudTestsResultsFetching,
      reportFetching,
      testsResultsTotalsFetching,
      testsRunsCountByHourFetching,
      testsRunsTotalsFetching,
    ],
  );

  const dataFetched = useMemo(() => {
    if (!hasPermission('VIEW_TESTS')) {
      return !!report;
    }
    return (
      !!report &&
      !!cloudTests &&
      !!cloudTestsResults &&
      !!cloudTestRuns &&
      !!testsRunsCountByHour &&
      !!testsRunsTotals &&
      !!testsResultsTotals
    );
  }, [
    cloudTestRuns,
    cloudTests,
    cloudTestsResults,
    report,
    testsResultsTotals,
    testsRunsCountByHour,
    testsRunsTotals,
    hasPermission,
  ]);

  const { data: reportMerged, isLoading: reportMergedLoading } = useQuery(
    [REPORT_MERGED, selectedEnvId],
    (): Promise<Report> => {
      if (!hasPermission('VIEW_TESTS')) {
        return Promise.resolve(report!);
      }
      return mergeReport(
        report!,
        cloudTests,
        cloudTestsResults,
        cloudTestRuns,
        testsRunsCountByHour,
        testsRunsTotals,
        testsResultsTotals,
      );
    },
    {
      enabled: !isFetching && dataFetched,
    },
  );

  return {
    data: reportMerged,
    isFetching: (reportFetching && !report) || reportMergedLoading,
    isError,
  };
};

export default useReport;
